import React, { useState } from "react";
import { motion } from "framer-motion";
import { LayoutContainer } from "../common";
import { Button } from "../ui";
import { Card, CardContent } from "../ui/card";
import { COLORED_IMAGES } from "../../images";

const SKILLS_DATA = {
  "UI/UX Design": {
    title: "UI/UX Design",
    description: [
      "Creating visually appealing and user-friendly interfaces that enhance engagement and usability. I focus on designing intuitive layouts, ensuring accessibility, and maintaining a seamless experience for users.",
      "With expertise in tools like Figma and Adobe XD, I translate ideas into wireframes and high-fidelity designs that align with modern design principles. My goal is to bridge the gap between design and functionality, working closely with developers to bring interactive prototypes to life."
    ],
    skills: [
      COLORED_IMAGES.figmaImg,
      COLORED_IMAGES.htmlImg,
      COLORED_IMAGES.cssImg,
      COLORED_IMAGES.bootImg,
      COLORED_IMAGES.sassImg,
    ],
  },
  "Web Development": {
    title: "Web Development",
    description: [
      "Building responsive and dynamic websites optimized for performance and accessibility. I specialize in creating modular, maintainable code using modern JavaScript frameworks like React and Next.js, ensuring efficient front-end architecture.",
      "By implementing best practices such as state management, lazy loading, and API integrations, I develop seamless web applications that enhance user engagement and experience. My expertise in TypeScript and Redux helps in maintaining scalable applications that are easy to manage and expand."
    ],
    skills: [
      COLORED_IMAGES.tsImg,
      COLORED_IMAGES.jsImg,
      COLORED_IMAGES.reactImg,
      COLORED_IMAGES.nextImg,
      COLORED_IMAGES.reduxImg,
      COLORED_IMAGES.npmImg,
      COLORED_IMAGES.gitImg,
    ],
  },
  "Mobile Development": {
    title: "Mobile Development",
    description: [
      "Developing cross-platform mobile applications using modern frameworks and best practices. I create mobile-first solutions that ensure seamless performance on both Android and iOS platforms.",
      "Leveraging React Native and TypeScript, I build scalable mobile apps with intuitive UIs and smooth interactions. By optimizing performance and managing dependencies effectively, I deliver apps that are efficient and maintainable."
    ],
    skills: [
      COLORED_IMAGES.tsImg,
      COLORED_IMAGES.reactImg,
      COLORED_IMAGES.npmImg,
      COLORED_IMAGES.gitImg,
    ],
  },
  "System Design": {
    title: "System Design",
    description: [
      "Crafting scalable and efficient system architectures that form the backbone of robust front-end solutions. I design systems that handle growth, security, and maintainability effectively.",
      "By focusing on modularity and performance, I structure applications that optimize resource usage and facilitate seamless integrations with various technologies."
    ],
    skills: [],
  },
};

export const Skills = () => {
  const [selectedCategory, setSelectedCategory] = useState(Object.keys(SKILLS_DATA)[0]);

  return (
    <LayoutContainer className="min-h-[83vh] flex-1 flex flex-col items-center">
      <motion.div
        initial={{ scaleY: 0 }}
        animate={{ scaleY: 1 }}
        exit={{ scaleY: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full flex-1 flex flex-col items-center justify-center h-[100%]"
      >
        <div className="flex justify-between items-center w-full mb-12">
          <h3 className="text-4xl font-bold text-primary whitespace-nowrap">Skills</h3>
          <div className="flex-1 h-[2px] bg-border ml-4"></div>
        </div>
        <div className="flex w-full gap-6">
          <div className="w-1/4 min-w-[220px] bg-background p-4 rounded-xl flex-shrink-0 border border-border">
            <h4 className="text-lg font-semibold text-primary mb-4">Categories</h4>
            <ul className="space-y-2">
              {Object.keys(SKILLS_DATA).map((category) => (
                <li key={category}>
                  <Button
                    variant={selectedCategory === category ? "secondary" : "ghost"}
                    className="w-full px-4 py-2 text-left flex justify-start items-center"
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category}
                  </Button>
                </li>
              ))}
            </ul>
          </div>

          <Card className="flex-1 bg-background border border-border flex-shrink-0">
            <CardContent className="p-6 h-[400px] w-full overflow-y-auto overflow-x-hidden flex flex-col justify-between">
              <DisplayedContent {...SKILLS_DATA[selectedCategory]} />
            </CardContent>
          </Card>
        </div>
      </motion.div>
    </LayoutContainer>
  );
};

const DisplayedContent = ({ title, description, skills }) => {
  return (
    <motion.div
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -10, opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="flex flex-col h-full justify-between"
    >
      <div>
        <h3 className="text-2xl font-semibold text-primary">{title}</h3>
        {description.map((paragraph, idx) => (
          <p key={idx} className="leading-relaxed my-4 text-gray-700 dark:text-gray-300 text-justify">{paragraph}</p>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-6">
        {skills.map((imgSrc, idx) => (
          <SkillItem key={idx} imgSrc={imgSrc} />
        ))}
      </div>
    </motion.div>
  );
};

const SkillItem = ({ imgSrc }) => {
  return (
    <div className="w-14 h-14 p-2 border rounded-lg shadow-sm bg-background flex items-center justify-center">
      <img src={imgSrc} alt="skill" className="w-full h-full object-contain" />
    </div>
  );
};
